import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { authAPI } from '@/api/new-api'
import { toastFactory } from '@/factories'
import { downloadHref, getApiErrorMessage } from '@/utils'

import { GetVisitorHistoryRequest } from './get-visitor-history'

export interface ExportVisitorHistoryRequest
  extends Omit<GetVisitorHistoryRequest, 'buildingId' | 'skip' | 'limit'> {
  buildingId: number
  fileName: string
}

export const exportVisitorHistory = async (
  params: Omit<ExportVisitorHistoryRequest, 'fileName'>,
) => {
  const response = await authAPI.get<Blob>('/visitor/export-csv', {
    params,
    responseType: 'blob',
  })
  return response.data
}

export const useExportVisitorHistory = () => {
  const objectUrlRef = useRef<string | null>(null)

  const mutation = useMutation({
    mutationFn: async ({ fileName, ...params }: ExportVisitorHistoryRequest) => {
      const blob = await exportVisitorHistory(params)
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = URL.createObjectURL(blob)
      downloadHref(objectUrlRef.current, fileName)
      return objectUrlRef.current
    },
    onError: (error) => {
      toastFactory.error(getApiErrorMessage(error))
    },
  })

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  return mutation
}
